// ============================================================================
// UTHMANIC FONT LOADER
// ============================================================================
// This script handles the loading and fallback of Uthmanic fonts
// Source: https://qurancomplex.gov.sa/techquran/dev/
// ============================================================================

class UthmanicFontLoader {
  constructor() {
    this.fontFamily = 'UthmanicHafs';
    this.fallbackFonts = [
      'UthmanicFallback',
      'Scheherazade New',
      'Amiri',
      'Noto Naskh Arabic',
      'Traditional Arabic'
    ];
    this.isLoaded = false;
    this.loadingElements = [];
    
    this.init();
  }

  init() {
    try {
      // Check if font is already loaded
      if (typeof document !== 'undefined' && document.fonts && document.fonts.check) {
        this.checkFontAvailability();
      } else {
        // Fallback for older browsers
        this.loadFontWithTimeout();
      }

      // Listen for font loading events
      this.setupFontLoading();
    } catch (error) {
      console.warn('Font loader init failed:', error);
      this.loadFontWithTimeout();
    }
  }

  checkFontAvailability() {
    try {
      if (typeof document === 'undefined' || !document.body) {
        this.loadFontWithTimeout();
        return;
      }

      const testString = 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ';
      const testElement = document.createElement('span');
      testElement.style.fontFamily = this.fontFamily;
      testElement.style.visibility = 'hidden';
      testElement.style.position = 'absolute';
      testElement.textContent = testString;
      document.body.appendChild(testElement);

      const originalWidth = testElement.offsetWidth;
      testElement.style.fontFamily = this.fallbackFonts.join(', ');
      const fallbackWidth = testElement.offsetWidth;

      document.body.removeChild(testElement);

      if (originalWidth !== fallbackWidth) {
        this.fontLoaded();
      } else {
        this.loadFontWithTimeout();
      }
    } catch (error) {
      console.warn('Font availability check failed:', error);
      this.loadFontWithTimeout();
    }
  }

  loadFontWithTimeout() {
    try {
      // Use FontFace API if available
      if (typeof window !== 'undefined' && window.FontFace) {
        this.loadWithFontFaceAPI();
      } else {
        // Fallback to CSS loading
        this.loadWithCSS();
      }
    } catch (error) {
      console.warn('Font loading with timeout failed:', error);
      this.loadWithCSS();
    }
  }

  loadWithFontFaceAPI() {
    try {
      if (typeof window === 'undefined' || !window.FontFace) {
        this.loadWithCSS();
        return;
      }

      const fontUrl = 'https://fonts.qurancomplex.gov.sa/UthmanicHafs1Ver18.woff2';
      
      const font = new FontFace(this.fontFamily, `url(${fontUrl})`, {
        style: 'normal',
        weight: 'normal',
        display: 'swap'
      });

      font.load().then(() => {
        if (typeof document !== 'undefined' && document.fonts) {
          document.fonts.add(font);
        }
        this.fontLoaded();
      }).catch((error) => {
        console.warn('Uthmanic font loading failed:', error);
        this.loadWithCSS();
      });
    } catch (error) {
      console.warn('FontFace API loading failed:', error);
      this.loadWithCSS();
    }
  }

  loadWithCSS() {
    try {
      if (typeof document === 'undefined' || !document.head) {
        this.fontLoaded();
        return;
      }

      // Create a link element to load the font
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.qurancomplex.gov.sa/UthmanicHafs1Ver18.css';
      
      link.onload = () => {
        this.fontLoaded();
      };
      
      link.onerror = () => {
        console.warn('Uthmanic font CSS loading failed');
        this.fontLoaded(); // Continue with fallback
      };

      document.head.appendChild(link);
    } catch (error) {
      console.warn('CSS font loading failed:', error);
      this.fontLoaded();
    }
  }

  setupFontLoading() {
    try {
      // Monitor font loading with Font Loading API
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this.fontLoaded();
        });
      }

      // Fallback: check periodically
      setTimeout(() => {
        if (!this.isLoaded) {
          this.fontLoaded();
        }
      }, 3000);
    } catch (error) {
      console.warn('Font loading setup failed:', error);
      // Fallback: check periodically
      setTimeout(() => {
        if (!this.isLoaded) {
          this.fontLoaded();
        }
      }, 3000);
    }
  }

  fontLoaded() {
    if (this.isLoaded) return;
    
    this.isLoaded = true;
    
    try {
      // Update all Quran text elements
      if (typeof document !== 'undefined') {
        const quranElements = document.querySelectorAll('.quran-text');
        quranElements.forEach(element => {
          element.classList.remove('font-loading');
          element.classList.add('font-loaded');
        });

        // Dispatch custom event
        const event = new CustomEvent('uthmanicFontLoaded', {
          detail: { fontFamily: this.fontFamily }
        });
        document.dispatchEvent(event);
      }
    } catch (error) {
      console.warn('Font loaded event handling failed:', error);
    }
  }

  // Public method to check if font is loaded
  isFontLoaded() {
    return this.isLoaded;
  }

  // Public method to force font loading
  forceLoad() {
    this.fontLoaded();
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Function to check if text contains Arabic characters
function containsArabic(text) {
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  return arabicRegex.test(text);
}

// Function to apply Uthmanic font to specific elements
function applyUthmanicFont(selector) {
  try {
    if (typeof document !== 'undefined') {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (containsArabic(element.textContent)) {
          element.classList.add('quran-text', 'font-loading');
        }
      });
    }
  } catch (error) {
    console.warn('Apply Uthmanic font failed:', error);
  }
}

// Function to optimize text rendering for Quran verses
function optimizeQuranText() {
  try {
    if (typeof document !== 'undefined') {
      const quranElements = document.querySelectorAll('.quran-text');
      
      quranElements.forEach(element => {
        // Ensure proper text direction
        element.style.direction = 'rtl';
        element.style.textAlign = 'justify';
        
        // Add proper spacing for better readability
        element.style.wordSpacing = '0.1em';
        element.style.letterSpacing = '0.05em';
        
        // Enhanced styling for better readability
        element.style.fontWeight = 'bold';
        element.style.fontSize = '1.15em';
        element.style.lineHeight = '2.5';
        
        // Ensure proper text rendering
        element.style.textRendering = 'optimizeLegibility';
        element.style.webkitFontSmoothing = 'antialiased';
        element.style.mozOsxFontSmoothing = 'grayscale';
      });
    }
  } catch (error) {
    console.warn('Quran text optimization failed:', error);
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// Initialize font loader when DOM is ready
function initializeFontLoader() {
  try {
    if (typeof window !== 'undefined' && window.document) {
      window.uthmanicFontLoader = new UthmanicFontLoader();
      optimizeQuranText();
    }
  } catch (error) {
    console.warn('Font loader initialization failed:', error);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeFontLoader);
  } else {
    initializeFontLoader();
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    UthmanicFontLoader,
    containsArabic,
    applyUthmanicFont,
    optimizeQuranText
  };
}
